import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react'
import {
  onAuthStateChanged,
  signInWithPopup,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut as fbSignOut,
  type User,
} from 'firebase/auth'
import { auth, googleProvider, microsoftProvider } from './firebase'
import { apiFetch, publicFetch } from '../api/client'

// AuthState exposes the slice of Firebase auth the rest of the app needs.
// idTokenProvider returns a fresh ID token (Firebase handles caching +
// refresh under the hood) so api/client.ts can attach Authorization on
// every fetch without re-reading currentUser itself.
export interface AuthState {
  user: User | null
  loading: boolean
  signInGoogle: () => Promise<void>
  signInMicrosoft: () => Promise<void>
  signInEmail: (email: string, password: string) => Promise<void>
  signUpEmail: (email: string, password: string) => Promise<void>
  signOut: () => Promise<void>
  idTokenProvider: () => Promise<string | null>
  // Send (or resend) the inline 6-digit verification code to the signed-in
  // (unverified) user's email via console-api. Replaces the Firebase link flow.
  sendVerifyCode: () => Promise<void>
  // Submit the code the user entered. On success it sets email_verified server-
  // side, then force-refreshes the Firebase user + ID token so the very next
  // authed call carries email_verified=true (the CA3 gate passes). The forced
  // refresh lives HERE so callers can't ship a stale token.
  confirmVerifyCode: (code: string) => Promise<void>
  // Reload the Firebase user so a just-completed email verification is reflected
  // (emailVerified flips to true) without a full page reload, and force a fresh
  // ID token so the updated claim is attached to subsequent requests.
  refreshUser: () => Promise<void>
  // Forgot-password, step 1 (signed out): ask console-api to email a reset code.
  // Resolves the same way whether or not the email has a password account — the
  // server never reveals which.
  sendPasswordReset: (email: string) => Promise<void>
  // Forgot-password, step 2: redeem the code + set the new password, then sign
  // straight in with it (the server also marks the email verified, so the fresh
  // token clears the CA3 gate).
  confirmPasswordReset: (email: string, code: string, password: string) => Promise<void>
}

const AuthContext = createContext<AuthState | null>(null)

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null)
  // `loading` starts true so RequireAuth can hold off on redirecting
  // until Firebase has had a chance to restore the session from
  // IndexedDB on initial page load. Without this the user briefly
  // bounces through /login on every refresh.
  const [loading, setLoading] = useState(true)
  // Bumped by refreshUser() to force the context value to recompute after a
  // currentUser.reload() — reload() mutates emailVerified in place on the same
  // User instance and does NOT fire onAuthStateChanged.
  const [tick, setTick] = useState(0)

  useEffect(() => {
    return onAuthStateChanged(auth, (u) => {
      setUser(u)
      setLoading(false)
    })
  }, [])

  const idTokenProvider = useCallback(async () => {
    const u = auth.currentUser
    if (!u) return null
    return u.getIdToken()
  }, [])

  const value = useMemo<AuthState>(
    () => ({
      user,
      loading,
      signInGoogle: async () => {
        await signInWithPopup(auth, googleProvider)
      },
      signInMicrosoft: async () => {
        await signInWithPopup(auth, microsoftProvider)
      },
      signInEmail: async (email, password) => {
        await signInWithEmailAndPassword(auth, email, password)
      },
      signUpEmail: async (email, password) => {
        // Just create the account. Verification is now an inline 6-digit code
        // delivered by console-api (sendVerifyCode below), not Firebase's link
        // flow — the SPA drives /v1/me/verify-email/{send,confirm} and the
        // server sets email_verified via the Admin SDK (CLAUDE.md §6).
        await createUserWithEmailAndPassword(auth, email, password)
      },
      signOut: async () => {
        await fbSignOut(auth)
      },
      sendVerifyCode: async () => {
        await apiFetch<{ status: string }>('/v1/me/verify-email/send', idTokenProvider, {
          method: 'POST',
        })
      },
      confirmVerifyCode: async (code: string) => {
        await apiFetch<{ status: string }>('/v1/me/verify-email/confirm', idTokenProvider, {
          method: 'POST',
          body: { code },
        })
        // Reload flips emailVerified on the local User; the forced token refresh
        // makes the NEXT apiFetch carry the updated email_verified claim (R1/D12)
        // so the CA3 gate passes without a manual reload.
        await auth.currentUser?.reload()
        await auth.currentUser?.getIdToken(true)
        setTick((t) => t + 1)
      },
      refreshUser: async () => {
        await auth.currentUser?.reload()
        await auth.currentUser?.getIdToken(true)
        setTick((t) => t + 1)
      },
      sendPasswordReset: async (email: string) => {
        await publicFetch<{ status: string }>('/v1/password-reset/send', {
          method: 'POST',
          body: { email },
        })
      },
      confirmPasswordReset: async (email: string, code: string, password: string) => {
        await publicFetch<{ status: string }>('/v1/password-reset/confirm', {
          method: 'POST',
          body: { email, code, password },
        })
        await signInWithEmailAndPassword(auth, email, password)
      },
      idTokenProvider,
    }),
    [user, loading, idTokenProvider, tick],
  )

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

// useAuth returns the auth state. Throws if used outside AuthProvider —
// a programmer error that should surface loudly during dev, not silently
// return a "logged out" state.
export function useAuth(): AuthState {
  const ctx = useContext(AuthContext)
  if (!ctx) {
    throw new Error('useAuth must be used inside <AuthProvider>')
  }
  return ctx
}
